import {Injectable} from '@angular/core';
import {FirebaseApp, initializeApp} from 'firebase/app';
import {Database, get, getDatabase, ref} from 'firebase/database';
import {deleteToken, getMessaging, getToken, Messaging} from 'firebase/messaging';
import {environment} from '../../environments/environment';
import {Auth, getAuth, signInAnonymously, signOut} from 'firebase/auth';
import {addDoc, collection, deleteDoc, doc, getDocs, getFirestore, limit, onSnapshot, orderBy, query, serverTimestamp, where, writeBatch} from 'firebase/firestore';
import {ChatCollectionRoot} from '../home/services/chat-channel.types';


@Injectable({providedIn: 'root'})

export class FirebaseService {
    private _app: FirebaseApp;
    private _auth: Auth;
    private _db: Database;
    private _messaging?: Messaging;
    private _firestore?: any;
    private _messagingTokenPromise?: Promise<string | null>;


    constructor() {
        this._app = initializeApp(environment.firebase);
        this._auth = getAuth(this._app);
        this._db = getDatabase(this._app, environment.firebase.databaseURL);
        this._firestore = getFirestore(this._app, environment.firebase.firestoreDatabase);

    }

    pruebaGetChats() {
        signInAnonymously(this._auth)
            .then(async () => {
                console.log('Autenticadoo');
                const querySnapshot = await getDocs(collection(this._firestore, 'chats'));
                querySnapshot.forEach((doc) => {
                    console.log(`${doc.id} => ${doc.data()}`);
                });


            })
            .catch((error) => {
                const errorCode = error.code;
                const errorMessage = error.message;
                console.log('errorrr', error);
            });
    }

    async getMessages(idChat: string | number, collectionRoot: ChatCollectionRoot = 'chats'): Promise<any[]> {
        try {
            await signInAnonymously(this._auth);
            const q = query(collection(this._firestore, `${collectionRoot}/${idChat}/messages`), orderBy('created_at', 'asc'));
            const querySnapshot = await getDocs(q);
            const messages: any[] = [];
            querySnapshot.forEach((doc) => {
                messages.push({id: doc.id, ...doc.data()});
            });
            return messages;
        } catch (error) {
            console.log('error getMessages', error);
            return [];
        }
    }

    async getWriteBatch() {
        await this.ensureAuth();
        return writeBatch(this._firestore);
    }

    async getCollection(path: string) {
        await this.ensureAuth();
        return collection(this._firestore, path);
    }

    onChat(idChat: string, handler: (chat: any) => void, collectionRoot: ChatCollectionRoot = 'chats'): () => void {
        signInAnonymously(this._auth).then(() => {
            const ref = doc(this._firestore, `${collectionRoot}/${idChat}`);

            return onSnapshot(ref, (snapshot) => {
                handler({id: snapshot.id, ...snapshot.data()});
            });
        });

        return () => {};
    }

    onMessages(idChat: string | number, handler: (messages: any[]) => void, collectionRoot: ChatCollectionRoot = 'chats'): () => void {
        // Autenticar antes de hacer la consulta
        signInAnonymously(this._auth).then(() => {
            const q = query(collection(this._firestore, `${collectionRoot}/${idChat}/messages`), orderBy('created_at', 'asc'));
            return onSnapshot(q, (querySnapshot) => {
                const messages: any[] = [];
                querySnapshot.forEach((doc) => {
                    messages.push({id: doc.id, ...doc.data()});
                });
                handler(messages);
            }, (error) => {
                console.log('error onMessages', error);
                handler([]);
            });
        }).catch((error) => {
            console.log('error auth onMessages', error);
            handler([]);
        });

        // Retornar función de cleanup (se ejecutará cuando se desmonte el componente)
        return () => {
        };
    }

    async sendMessage(idChat: string | number, message: string, meta?: { senderName?: string, senderType?: string }, collectionRoot: ChatCollectionRoot = 'chats'): Promise<void> {
        if (!message || String(message).trim() === '') {
            return;
        }
        try {
            await signInAnonymously(this._auth);
            await addDoc(collection(this._firestore, `${collectionRoot}/${idChat}/messages`), {
                message: String(message).trim(),
                sender_name: meta?.senderName ?? 'Usuario',
                sender_type: meta?.senderType ?? 'gpv',
                created_at: serverTimestamp(),
                gpv_read: true,
                pdv_read: false,
                distribuidor_read: false,
            });
        } catch (error) {
            console.log('error sendMessage', error);
        }
    }

    get app(): FirebaseApp {
        return this._app;
    }

    get db(): Database {
        return this._db;
    }

    async checkConnection(): Promise<boolean> {
        try {
            const snap = await get(ref(this._db, '.info/connected'));
            return !!snap.val();
        } catch {
            return false;
        }
    }

    async registerMessagingToken(): Promise<string | null> {
        if (this._messagingTokenPromise) {
            return this._messagingTokenPromise;
        }

        this._messagingTokenPromise = (async () => {
            try {
                if (!('Notification' in window)) {
                    console.log('🚫 Este navegador no soporta notificaciones push');
                    return null;
                }

                const messaging = getMessaging(this._app);
                if (!messaging) {
                    console.log('🚫 Firebase messaging no está disponible');
                    return null;
                }

                const permission = await Notification.requestPermission();
                console.log('🔔 Permiso de notificación:', permission);

                if (permission === 'granted') {
                    const token = await getToken(messaging);
                    if (token) {
                        console.log('🎯 TOKEN DE FIREBASE MESSAGING REGISTRADO:', token);
                        console.log('📅 Fecha de registro:', new Date().toLocaleString());
                        return token;
                    }
                }
                return null;
            } catch (error) {
                console.error('❌ Error al registrar token de Firebase:', error);
                return null;
            }
        })();

        return this._messagingTokenPromise;
    }

    /**
     * Limpia FCM del dispositivo + sesión Auth anónima (IndexedDB),
     * para que al entrar otro usuario no se reutilice el token de Firebase Auth.
     * Llamar DESPUÉS del remove-fcm-token al backend.
     */
    async clearSessionOnLogout(): Promise<void> {
        try {
            if ('Notification' in window && Notification.permission === 'granted') {
                const messaging = getMessaging(this._app);
                await deleteToken(messaging);
            }
        } catch {
            // No bloquear logout si falla deleteToken
        } finally {
            this._messagingTokenPromise = undefined;
            this._messaging = undefined;
        }

        try {
            await signOut(this._auth);
        } catch {
            // No bloquear logout si falla signOut
        }
    }

    async getDocument(path: string) {
        await this.ensureAuth();
        return doc(this._firestore, path);
    }

    async deleteDocument(path: string) {
        await this.ensureAuth();
        await deleteDoc(await this.getDocument(path));
    }

    async getLastMessage(chatId: string, collectionRoot: ChatCollectionRoot = 'chats'): Promise<any | null> {
        const messagesRef = collection(this._firestore, `${collectionRoot}/${chatId}/messages`);
        const q = query(messagesRef, orderBy('created_at', 'desc'), limit(1));

        const querySnapshot = await getDocs(q);

        if (!querySnapshot.empty) {
            return querySnapshot.docs[0].data();
        } else {
            return null;
        }
    }

    private async ensureAuth() {
        if (!this._auth.currentUser) {
            await signInAnonymously(this._auth);
        }
    }

    async getNewChat(pdv_id: any, handler: (snapshot: any) => void, collectionRoot: ChatCollectionRoot = 'chats') {
        const chatsRef = await this.getCollection(collectionRoot);
        const constraints = [
            where('id_pdv', '==', parseInt(pdv_id)),
            where('archived', '==', false)
        ];
        const chatQuery = query(chatsRef, ...constraints);

        return onSnapshot(chatQuery, (snapshot) => handler(snapshot));
    }
}
